// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const TEAM = [
  { name: "Jordan Lutz", initials: "JL" },
  { name: "Ryan Miskulin", initials: "RM" },
  { name: "Taylor Schenher", initials: "TS" },
  { name: "Des Bachynski", initials: "DB" },
  { name: "Lucas Mason", initials: "LM" },
  { name: "Lee Mehling", initials: "LMe" },
  { name: "Clorice Robertson", initials: "CR" },
  { name: "Tyson Becker", initials: "TB" },
  { name: "Tyler Hodgson", initials: "TH" },
];

const SPRINTS = [
  { id: 1, name: "Sprint 12", startDate: "2026-01-27", endDate: "2026-02-07", status: "completed", velocity: 42, capacity: 50 },
  { id: 2, name: "Sprint 13", startDate: "2026-02-10", endDate: "2026-02-21", status: "active", velocity: 0, capacity: 48 },
  { id: 3, name: "Sprint 14", startDate: "2026-02-24", endDate: "2026-03-06", status: "planned", velocity: 0, capacity: 50 },
];

const SPRINT_TASKS = [
  { id: 1, title: "Implement real-time notifications", points: 8, assignee: "Jordan Lutz", priority: "P0", column: "In Progress", sprint: 2 },
  { id: 2, title: "Q1 Revenue Forecast Model", points: 5, assignee: "Taylor Schenher", priority: "P0", column: "Review", sprint: 2 },
  { id: 3, title: "Canstorr partnership agreement", points: 5, assignee: "Tyson Becker", priority: "P1", column: "In Progress", sprint: 2 },
  { id: 4, title: "Sales deck refresh for Q1", points: 3, assignee: "Lucas Mason", priority: "P1", column: "Current Sprint", sprint: 2 },
  { id: 5, title: "Mining compliance audit", points: 5, assignee: "Lee Mehling", priority: "P1", column: "Backlog", sprint: 2 },
  { id: 6, title: "Health platform beta testing", points: 8, assignee: "Des Bachynski", priority: "P1", column: "In Progress", sprint: 2 },
  { id: 7, title: "Overwatch dashboard enhancements", points: 5, assignee: "Tyler Hodgson", priority: "P2", column: "Current Sprint", sprint: 2 },
  { id: 8, title: "Board presentation Q1 strategy", points: 3, assignee: "Ryan Miskulin", priority: "P0", column: "Review", sprint: 2 },
  { id: 9, title: "AR reconciliation January", points: 3, assignee: "Clorice Robertson", priority: "P2", column: "Done", sprint: 2 },
  { id: 10, title: "Insurance broker lead campaign", points: 5, assignee: "Lucas Mason", priority: "P2", column: "In Progress", sprint: 2 },
  { id: 11, title: "Database performance optimization", points: 8, assignee: "Jordan Lutz", priority: "P1", column: "Backlog", sprint: 3 },
  { id: 12, title: "Fleet management API integration", points: 8, assignee: "Jordan Lutz", priority: "P1", column: "Backlog", sprint: 3 },
  { id: 13, title: "Security pen testing", points: 5, assignee: "Tyler Hodgson", priority: "P0", column: "Backlog", sprint: 3 },
  { id: 14, title: "Employee benefits renewal", points: 3, assignee: "Clorice Robertson", priority: "P2", column: "Backlog", sprint: 3 },
  { id: 15, title: "Commodities market report Q1", points: 5, assignee: "Tyson Becker", priority: "P2", column: "Backlog", sprint: 3 },
];

const COLUMNS = ["Backlog", "Current Sprint", "In Progress", "Review", "Done"];

export default function SprintsPage() {
  const [tasks, setTasks] = useState(SPRINT_TASKS);
  const [selectedSprint, setSelectedSprint] = useState(2);
  const [movingTask, setMovingTask] = useState(null);

  const sprint = SPRINTS.find(s => s.id === selectedSprint);
  const sprintTasks = tasks.filter(t => t.sprint === selectedSprint);
  const totalPoints = sprintTasks.reduce((s, t) => s + t.points, 0);
  const donePoints = sprintTasks.filter(t => t.column === "Done").reduce((s, t) => s + t.points, 0);
  const inProgressPoints = sprintTasks.filter(t => t.column === "In Progress").reduce((s, t) => s + t.points, 0);
  const reviewPoints = sprintTasks.filter(t => t.column === "Review").reduce((s, t) => s + t.points, 0);

  const moveTask = (taskId, newColumn) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, column: newColumn } : t));
    setMovingTask(null);
  };

  const burndownData = [
    { day: "Day 1", ideal: totalPoints, actual: totalPoints },
    { day: "Day 2", ideal: totalPoints - Math.round(totalPoints / 10), actual: totalPoints - 3 },
    { day: "Day 3", ideal: totalPoints - Math.round(totalPoints / 10 * 2), actual: totalPoints - 5 },
    { day: "Day 4", ideal: totalPoints - Math.round(totalPoints / 10 * 3), actual: totalPoints - 8 },
    { day: "Day 5", ideal: totalPoints - Math.round(totalPoints / 10 * 4), actual: totalPoints - 11 },
    { day: "Day 6", ideal: totalPoints - Math.round(totalPoints / 10 * 5), actual: totalPoints - 16 },
    { day: "Day 7", ideal: totalPoints - Math.round(totalPoints / 10 * 6), actual: totalPoints - 19 },
    { day: "Day 8", ideal: totalPoints - Math.round(totalPoints / 10 * 7), actual: totalPoints - donePoints },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1>Sprints</h1><p>Agile sprint management</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          {SPRINTS.map(s => (
            <button key={s.id} className={"btn " + (selectedSprint === s.id ? "btn-primary" : "btn-outline")} onClick={() => setSelectedSprint(s.id)}>
              {s.name} {s.status === "active" && <span className="badge badge-green" style={{ marginLeft: 6 }}>Active</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Sprint Progress</div>
          <div className="stat-value">{totalPoints > 0 ? Math.round(donePoints / totalPoints * 100) : 0}%</div>
          <div style={{ marginTop: 8, height: 6, background: "#334155", borderRadius: 3 }}>
            <div style={{ width: (totalPoints > 0 ? donePoints / totalPoints * 100 : 0) + "%", height: 6, background: "#10b981", borderRadius: 3 }} />
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Velocity</div>
          <div className="stat-value">{sprint.status === "completed" ? sprint.velocity : donePoints + reviewPoints}<span style={{ fontSize: 14, color: "#64748b" }}> pts</span></div>
          <div className="stat-change positive">Target: {sprint.capacity}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Remaining Points</div>
          <div className="stat-value" style={{ color: "#eab308" }}>{totalPoints - donePoints}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{donePoints} of {totalPoints} completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Team Capacity</div>
          <div className="stat-value">{sprint.capacity}<span style={{ fontSize: 14, color: "#64748b" }}> pts</span></div>
          <div style={{ fontSize: 12, color: totalPoints > sprint.capacity ? "#ef4444" : "#10b981" }}>{totalPoints > sprint.capacity ? "Over capacity" : "Within capacity"}</div>
        </div>
      </div>

      {/* Sprint Info Bar */}
      <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <div><span style={{ fontSize: 12, color: "#64748b" }}>Sprint</span><div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>{sprint.name}</div></div>
          <div><span style={{ fontSize: 12, color: "#64748b" }}>Dates</span><div style={{ fontSize: 13, color: "#e2e8f0" }}>{sprint.startDate} - {sprint.endDate}</div></div>
          <div><span className={"badge " + (sprint.status === "active" ? "badge-green" : sprint.status === "completed" ? "badge-blue" : "badge-yellow")}>{sprint.status}</span></div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>Tasks: <strong style={{ color: "#e2e8f0" }}>{sprintTasks.length}</strong></span>
          <span style={{ fontSize: 13, color: "#64748b" }}>Points: <strong style={{ color: "#e2e8f0" }}>{totalPoints}</strong></span>
        </div>
      </div>

      {/* Sprint Board */}
      <div className="kanban-board">
        {COLUMNS.map(col => {
          const colTasks = sprintTasks.filter(t => t.column === col);
          const colPoints = colTasks.reduce((s, t) => s + t.points, 0);
          return (
            <div key={col} className="kanban-column">
              <div className="kanban-column-header">
                <h3>{col}</h3>
                <span className="count">{colTasks.length} ({colPoints} pts)</span>
              </div>
              <div className="kanban-cards">
                {colTasks.map(task => (
                  <div key={task.id} className="kanban-card" onClick={() => setMovingTask(movingTask === task.id ? null : task.id)}>
                    <div className="kanban-card-title">{task.title}</div>
                    <div className="kanban-card-meta">
                      <span className={"priority-tag priority-" + task.priority.toLowerCase()}>{task.priority}</span>
                      <span className="assignee-tag">{TEAM.find(t => t.name === task.assignee)?.initials}</span>
                      <span style={{ fontSize: 11, color: "#10b981", fontWeight: 600 }}>{task.points} pts</span>
                    </div>
                    {movingTask === task.id && (
                      <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {COLUMNS.filter(c => c !== col).map(c => (
                          <button key={c} className="btn btn-outline btn-sm" style={{ fontSize: 10, padding: "2px 6px" }} onClick={(e) => { e.stopPropagation(); moveTask(task.id, c); }}>{c}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Burndown Chart (Text-Based) */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Burndown Chart</h3></div>
        <div style={{ padding: "0 8px" }}>
          {burndownData.map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: "1px solid #1e293b" }}>
              <span style={{ width: 50, fontSize: 12, color: "#64748b" }}>{d.day}</span>
              <div style={{ flex: 1, position: "relative", height: 20 }}>
                <div style={{ position: "absolute", top: 0, left: 0, width: (d.ideal / totalPoints * 100) + "%", height: 8, background: "#334155", borderRadius: 4 }} />
                <div style={{ position: "absolute", top: 10, left: 0, width: (d.actual / totalPoints * 100) + "%", height: 8, background: d.actual > d.ideal ? "#ef4444" : "#10b981", borderRadius: 4 }} />
              </div>
              <div style={{ width: 80, textAlign: "right" }}>
                <span style={{ fontSize: 11, color: "#64748b" }}>I:{d.ideal}</span>
                <span style={{ fontSize: 11, color: d.actual > d.ideal ? "#ef4444" : "#10b981", marginLeft: 8 }}>A:{d.actual}</span>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11, color: "#64748b" }}>
            <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#334155", borderRadius: 2, marginRight: 4 }} />Ideal</span>
            <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#10b981", borderRadius: 2, marginRight: 4 }} />Actual</span>
          </div>
        </div>
      </div>

      {/* Team Load */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Team Load - {sprint.name}</h3></div>
        <div className="chart-bar-container">
          {TEAM.map(member => {
            const memberTasks = sprintTasks.filter(t => t.assignee === member.name);
            const memberPoints = memberTasks.reduce((s, t) => s + t.points, 0);
            const maxPoints = 13;
            return (
              <div key={member.name} className="chart-bar-row">
                <span className="chart-bar-label" style={{ width: 120 }}>{member.initials} - {member.name.split(" ")[0]}</span>
                <div className="chart-bar-track">
                  <div className={"chart-bar-fill" + (memberPoints > 10 ? " red" : memberPoints > 6 ? " orange" : "")} style={{ width: Math.min(memberPoints / maxPoints * 100, 100) + "%" }}>
                    {memberPoints > 0 ? memberPoints + " pts (" + memberTasks.length + " tasks)" : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Velocity History */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Velocity History</h3></div>
        <div className="chart-bar-container">
          {[
            { sprint: "Sprint 9", velocity: 36 },
            { sprint: "Sprint 10", velocity: 40 },
            { sprint: "Sprint 11", velocity: 38 },
            { sprint: "Sprint 12", velocity: 42 },
            { sprint: "Sprint 13", velocity: donePoints, label: "(in progress)" },
          ].map((s, i) => (
            <div key={i} className="chart-bar-row">
              <span className="chart-bar-label">{s.sprint}</span>
              <div className="chart-bar-track">
                <div className={"chart-bar-fill" + (i === 4 ? " blue" : "")} style={{ width: (s.velocity / 50 * 100) + "%" }}>
                  {s.velocity} pts {s.label || ""}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>Average Velocity: <strong style={{ color: "#e2e8f0" }}>39 pts/sprint</strong></div>
      </div>
    </div>
  );
}
